import { useLocalStorage } from '@mantine/hooks';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import React, { ReactNode, useEffect, useMemo } from 'react';
import { LuChevronDown } from 'react-icons/lu';
import { Area, AreaChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDate } from '@/utils/date-formatter';
import { formatCurrency, formatCurrencyShort } from '@/utils/number-formatter';

import { useCommandCenterHouseholds } from '../household/household-queries';
import { useAggregatedInflows, usePracticePulse } from '../portfolio/reporting-queries';
import { DashboardCard } from './DashboardCard';
import {
    DEFAULT_PRACTICE_PULSE_PERIOD,
    getPracticePulseDateRange,
    normalizePracticePulsePeriod,
    PRACTICE_PULSE_PERIOD_OPTIONS,
    PRACTICE_PULSE_PERIOD_STORAGE_KEY,
} from './practice-pulse-periods';

const formatYAxis = (value: number) => formatCurrencyShort(value, 'USD', 1);

const formatXAxis = (dateStr: unknown, spanDays: number) => {
    if (!dateStr || typeof dateStr !== 'string') return '';
    const parsedDate = parseISO(dateStr);
    if (Number.isNaN(parsedDate.getTime())) return '';

    if (spanDays <= 31) {
        return format(parsedDate, 'MMM d');
    }
    if (spanDays <= 366) {
        return format(parsedDate, 'MMM');
    }
    return dateStr.slice(0, 4);
};

export const PracticePulse = () => {
    const [storedPeriod, setStoredPeriod] = useLocalStorage<string>({
        key: PRACTICE_PULSE_PERIOD_STORAGE_KEY,
        defaultValue: DEFAULT_PRACTICE_PULSE_PERIOD,
    });

    const selectedPeriod = normalizePracticePulsePeriod(storedPeriod);

    useEffect(() => {
        if (storedPeriod !== selectedPeriod) {
            setStoredPeriod(selectedPeriod);
        }
    }, [selectedPeriod, setStoredPeriod, storedPeriod]);

    const range = useMemo(() => getPracticePulseDateRange(selectedPeriod), [selectedPeriod]);
    const { from: aumStart, to: aumEnd } = range;

    const { data, isLoading } = usePracticePulse(aumStart, aumEnd);
    const { data: inflowsData, isLoading: isInflowsLoading } = useAggregatedInflows(aumStart, aumEnd);
    const { data: households, isLoading: isHouseholdsLoading } = useCommandCenterHouseholds();

    const spanDays = useMemo(() => differenceInCalendarDays(aumEnd, aumStart), [aumEnd, aumStart]);

    const chartData = useMemo(() => {
        if (!data?.dailyAum) return [];
        return data.dailyAum.map((entry) => ({
            date: entry.date,
            aum: Number(entry.aum),
        }));
    }, [data?.dailyAum]);

    const xTicks = useMemo(() => {
        if (spanDays <= 31) {
            const step = Math.max(1, Math.ceil(chartData.length / 6));

            return chartData
                .filter((entry, index) => index === 0 || index === chartData.length - 1 || index % step === 0)
                .map((entry) => entry.date);
        }

        const seen = new Set<string>();
        const ticks: string[] = [];
        for (const entry of chartData) {
            const key = spanDays <= 366 ? entry.date.slice(0, 7) : entry.date.slice(0, 4);
            if (!seen.has(key)) {
                seen.add(key);
                ticks.push(entry.date);
            }
        }
        return ticks;
    }, [chartData, spanDays]);

    const totalAum = data?.totalAum != null ? Number(data.totalAum) : undefined;
    const inflows = inflowsData != null ? Number(inflowsData) : undefined;
    const householdCount = households?.reduce((acc, h) => acc + (h.aum ? 1 : 0), 0);
    const selectedRangeLabel = `${formatDate(aumStart, 'HUMAN')} – ${formatDate(aumEnd, 'HUMAN')}`;
    const flowsLabel = (
        <span className="inline-flex items-center gap-1">
            <span>Flows</span>
            <PeriodSelector selectedPeriod={selectedPeriod} onSelect={setStoredPeriod} />
        </span>
    );

    return (
        <DashboardCard title="Practice Pulse" size="full" className="border-0 p-0 py-6 pb-30">
            <div className="flex flex-col gap-4">
                <div className="flex flex-wrap gap-8">
                    <Metric
                        label="Total AUM"
                        value={totalAum !== undefined ? formatCurrencyShort(totalAum, 'USD', 1) : undefined}
                        isLoading={isLoading}
                    />
                    <Metric
                        label={flowsLabel}
                        value={inflows !== undefined ? formatCurrencyShort(inflows, 'USD', 1) : undefined}
                        isLoading={isInflowsLoading}
                    />
                    <Metric
                        label="Households"
                        value={householdCount !== undefined ? String(householdCount) : undefined}
                        isLoading={isHouseholdsLoading}
                    />
                </div>

                <div className="text-muted-foreground text-xs">{selectedRangeLabel}</div>

                <div className="h-56 w-full">
                    {isLoading ? (
                        <div className="h-full w-full animate-pulse rounded-md bg-gray-100" />
                    ) : chartData.length === 0 ? (
                        <div className="text-muted-foreground flex h-full w-full items-center justify-center text-sm">
                            No AUM history available.
                        </div>
                    ) : (
                        <AreaChart
                            className="[&_*]:!outline-none"
                            responsive
                            style={{ width: '100%', height: '100%' }}
                            data={chartData}
                            margin={{ top: 4, right: 0, left: 8, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient id="practicePulseGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#A8D8DC" stopOpacity={1} />
                                    <stop offset="95%" stopColor="#A8D8DC" stopOpacity={1} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                            <XAxis
                                dataKey="date"
                                ticks={xTicks}
                                tickFormatter={(d) => formatXAxis(d, spanDays)}
                                tick={{ fontSize: 11, fill: '#9CA3AF' }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                tickFormatter={formatYAxis}
                                tick={{ fontSize: 11, fill: '#9CA3AF' }}
                                axisLine={false}
                                tickLine={false}
                                width={56}
                                orientation="right"
                            />
                            <Tooltip
                                formatter={(value) => [formatCurrency(Array.isArray(value) ? value[0] : value), 'AUM']}
                                labelFormatter={(label) =>
                                    typeof label === 'string' ? formatDate(label, 'HUMAN') : label
                                }
                                contentStyle={{
                                    fontSize: 12,
                                    borderRadius: 6,
                                    border: '1px solid #E5E7EB',
                                }}
                            />
                            <Area
                                type="monotone"
                                dataKey="aum"
                                stroke="#3C8A95"
                                strokeWidth={1.5}
                                fill="url(#practicePulseGradient)"
                                dot={false}
                                activeDot={{ r: 4, fill: '#3C8A95' }}
                            />
                        </AreaChart>
                    )}
                </div>
            </div>
        </DashboardCard>
    );
};

interface MetricProps {
    label: ReactNode;
    value: string | undefined;
    isLoading: boolean;
}

interface PeriodSelectorProps {
    selectedPeriod: string;
    onSelect: (value: string) => void;
}

const PeriodSelector = ({ selectedPeriod, onSelect }: PeriodSelectorProps) => (
    <DropdownMenu>
        <DropdownMenuTrigger asChild>
            <button
                type="button"
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex cursor-pointer items-center gap-1 rounded-sm underline decoration-dotted underline-offset-3 outline-none focus-visible:ring-2"
                aria-label={`Select flows time period. Current selection: ${selectedPeriod}`}
            >
                <span>{selectedPeriod}</span>
                <LuChevronDown className="h-3 w-3" />
            </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuRadioGroup value={selectedPeriod} onValueChange={onSelect}>
                {PRACTICE_PULSE_PERIOD_OPTIONS.map((period) => (
                    <DropdownMenuRadioItem key={period} value={period}>
                        {period}
                    </DropdownMenuRadioItem>
                ))}
            </DropdownMenuRadioGroup>
        </DropdownMenuContent>
    </DropdownMenu>
);

const Metric = ({ label, value, isLoading }: MetricProps) => (
    <div className="text-cocoa-1000 flex max-w-full min-w-0 flex-col gap-1">
        <span className="text-muted-foreground text-xs">{label}</span>
        <span
            className={`text-foreground block max-w-full truncate text-2xl font-thin ${
                isLoading ? 'ndvr-skeleton text-transparent' : ''
            }`}
            title={value}
        >
            {value ?? '—'}
        </span>
    </div>
);
